// Writer — version-scrubber actions (revert / fork).
//
// Both actions are strictly additive: they only INSERT new rows into
// `writer_versions` (revert) or `writer_projects` + `writer_versions`
// (fork). Original history is never mutated or deleted — Writer's linear
// version log stays append-only, which matches the plan's "snapshot every
// accepted turn" contract and keeps undo safe.
//
// RLS on `writer_versions` / `writer_projects` already enforces
// `is_writer_admin_owner(auth.uid())`, so these helpers can run from the
// client without an edge function.

import { supabase } from '@/integrations/supabase/client';

export interface RevertResult {
  version_id: string;
}

export interface ForkResult {
  project_id: string;
  version_id: string;
}

/**
 * Create a new pending version whose `main_tex` is a copy of the given
 * historical version. Non-destructive — the target version is untouched.
 */
export async function revertToVersion(params: {
  projectId: string;
  versionId: string;
}): Promise<RevertResult> {
  const { projectId, versionId } = params;

  const { data: source, error: srcErr } = await supabase
    .from('writer_versions')
    .select('id, main_tex, bib_source, label')
    .eq('id', versionId)
    .eq('project_id', projectId)
    .maybeSingle();
  if (srcErr) throw srcErr;
  if (!source) throw new Error('Source version not found');

  const {
    data: { user },
    error: authErr,
  } = await supabase.auth.getUser();
  if (authErr || !user) throw new Error('Not authenticated');

  const label = source.label
    ? `Reverted to “${source.label}”`
    : 'Reverted version';

  const { data: inserted, error: insErr } = await supabase
    .from('writer_versions')
    .insert({
      project_id: projectId,
      owner_id: user.id,
      parent_version_id: source.id,
      label,
      main_tex: source.main_tex ?? '',
      bib_source: (source as { bib_source?: string }).bib_source ?? '',
      compile_status: 'pending',
    })
    .select('id')
    .single();
  if (insErr || !inserted) throw insErr ?? new Error('Failed to insert version');

  return { version_id: inserted.id };
}

/**
 * Fork a project at a specific version: create a new `writer_projects`
 * row seeded with the version's `main_tex`. Returns the new project id
 * so the caller can navigate to it.
 */
export async function forkFromVersion(params: {
  projectId: string;
  versionId: string;
  titleSuffix?: string;
}): Promise<ForkResult> {
  const { projectId, versionId, titleSuffix = 'fork' } = params;

  const { data: source, error: srcErr } = await supabase
    .from('writer_versions')
    .select('id, main_tex, bib_source, label')
    .eq('id', versionId)
    .eq('project_id', projectId)
    .maybeSingle();
  if (srcErr) throw srcErr;
  if (!source) throw new Error('Source version not found');

  const { data: sourceProject, error: projErr } = await supabase
    .from('writer_projects')
    .select('id, title')
    .eq('id', projectId)
    .maybeSingle();
  if (projErr) throw projErr;
  if (!sourceProject) throw new Error('Source project not found');

  const {
    data: { user },
    error: authErr,
  } = await supabase.auth.getUser();
  if (authErr || !user) throw new Error('Not authenticated');

  const forkedTitle = `${sourceProject.title} (${titleSuffix})`;

  const { data: newProject, error: newProjErr } = await supabase
    .from('writer_projects')
    .insert({
      owner_id: user.id,
      title: forkedTitle,
    })
    .select('id')
    .single();
  if (newProjErr || !newProject) {
    throw newProjErr ?? new Error('Failed to create forked project');
  }

  const { data: newVersion, error: newVerErr } = await supabase
    .from('writer_versions')
    .insert({
      project_id: newProject.id,
      owner_id: user.id,
      parent_version_id: null,
      label: source.label ? `Forked from “${source.label}”` : 'Forked version',
      main_tex: source.main_tex ?? '',
      bib_source: (source as { bib_source?: string }).bib_source ?? '',
      compile_status: 'pending',
    })
    .select('id')
    .single();
  if (newVerErr || !newVersion) {
    throw newVerErr ?? new Error('Failed to create forked version');
  }

  return { project_id: newProject.id, version_id: newVersion.id };
}
